import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, MaskLineInView } from "./Reveal";
import { scrollToId as scrollTo } from "@/lib/scroll";
import { getExamples } from "@/lib/api";

const DEFAULT_EXAMPLES = [
  {
    title: "Gebäudereinigung",
    tag: "Beispiel · Dienstleister",
    img: "https://static.prod-images.emergentagent.com/jobs/34f410c9-c8c3-4ee7-89cd-86df06bf8d8b/images/ac88f5fbe21c8d8545681060f8936b1f84ef427549ad358bf4fe6ca4f4e2bcf8.jpeg",
    points: [
      "Klarer Fokus auf Anfragen statt Infoflut",
      "Vertrauen durch Referenzen & Bewertungen",
      "Ein starker Button, ein klares Ziel",
    ],
  },
  {
    title: "Autoaufbereitung",
    tag: "Beispiel · Automotive",
    img: "https://static.prod-images.emergentagent.com/jobs/34f410c9-c8c3-4ee7-89cd-86df06bf8d8b/images/94f411a7b9bf73132fc9f908ed9d58e8bc2bcf8237fe169e64f39106eab2f9c9.jpeg",
    points: [
      "Premium-Look für Premium-Preise",
      "Vorher-/Nachher-Wirkung überzeugt sofort",
      "Mobil optimiert – perfekt für Instagram-Ads",
    ],
  },
];

function BrowserFrame({ img, title }) {
  return (
    <div className="relative border border-white/10 bg-[#100b07] overflow-hidden shadow-2xl shadow-black/60">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[#0F0F0F]">
        <span className="h-3 w-3 rounded-full bg-[#FF3B00]/80" />
        <span className="h-3 w-3 rounded-full bg-neutral-600" />
        <span className="h-3 w-3 rounded-full bg-neutral-700" />
        <span className="ml-4 font-tech text-[11px] text-neutral-500 truncate">
          www.ihre-website.de
        </span>
      </div>
      <div className="relative overflow-hidden">
        <img src={img} alt={`Website-Beispiel ${title}`} className="w-full block" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#FF3B00]/[0.05] via-transparent to-transparent" />
      </div>
    </div>
  );
}

export default function Examples() {
  const ref = useRef(null);
  const [examples, setExamples] = useState(DEFAULT_EXAMPLES);

  useEffect(() => {
    getExamples()
      .then((data) => { if (Array.isArray(data) && data.length) setExamples(data); })
      .catch(() => {});
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="beispiele" ref={ref} className="relative py-14 lg:py-20 border-t border-white/10" data-testid="examples">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <Reveal className="font-tech text-xs uppercase tracking-[0.25em] text-[#FF3B00] mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[#FF3B00]" /> Design-Beispiele
            </Reveal>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight text-white uppercase">
              <MaskLineInView>So könnte Ihre</MaskLineInView>
              <MaskLineInView delay={0.1}>Website aussehen</MaskLineInView>
            </h2>
          </div>
          <Reveal delay={0.2} className="max-w-sm text-neutral-400 text-base leading-relaxed">
            Zwei Design-Beispiele aus unterschiedlichen Branchen – jede Website wird individuell
            gestaltet und auf ein klares Ziel optimiert: mehr Anfragen.
          </Reveal>
        </div>

        <div className="space-y-14">
          {examples.map((ex, i) => {
            const reverse = i % 2 === 1;
            return (
              <div key={ex.title} className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <motion.div
                  style={{ y }}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className={`lg:col-span-8 ${reverse ? "lg:order-2" : ""}`}
                  data-testid={`example-${i}`}
                >
                  <BrowserFrame img={ex.img || ex.image} title={ex.title} />
                </motion.div>

                <Reveal delay={0.15} className={`lg:col-span-4 ${reverse ? "lg:order-1" : ""}`}>
                  <p className="font-tech text-xs uppercase tracking-[0.18em] text-[#FF3B00] mb-4">{ex.tag}</p>
                  <h3 className="font-display text-4xl text-white mb-6">{ex.title}</h3>
                  <ul className="space-y-4 mb-8">
                    {ex.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-neutral-300 text-sm leading-relaxed">
                        <Check size={17} className="text-[#FF3B00] shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => scrollTo("#kontakt")}
                    className="font-tech text-xs uppercase tracking-[0.12em] text-white border-b-2 border-[#FF3B00] pb-1 hover:text-[#FF3B00] transition-colors"
                    data-testid={`example-cta-${i}`}
                  >
                    So eine Website will ich →
                  </button>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
